import React, { FunctionComponent, useState } from 'react';
import ReactApexChart from 'react-apexcharts';

import styles from './DataLabel.module.scss';
import { loadDataLabelChartData } from 'app/Requests';
import { time_interval } from 'app/components/TimeIntervalFilter/TimeIntervalFilter';
import SelectInput from 'app/components/Form/Inputs/SelectInput';
import DotsMenu from 'app/components/DotsMenu';
import { nFormatter } from 'app/utils/numbersFormater';
import Loader from 'app/components/Loader';
import NoData from 'app/components/NoData';
import { _t } from 'app/utils/i18n';
import CardTitle from 'app/components/CardTitle';
import { DonutChartData } from 'app/utils/types';
import { dateTimeFormatter } from 'app/utils/dateTimeFormatter';
import { useQuery } from 'react-query';
import { useAppSettings } from 'app/context/settings/appSettings';
import { isItArabicText } from 'app/utils/helpers';
import { getFromLocalStorage } from 'app/utils/localStorage';

interface Props {
  compId: string;
  showLegend?: boolean;
  data?: DonutChartData;
  slug?: string;
  showFilter?: boolean;
  customFilters?: any;
  tooltipTitle?: string;
  channelTooltip?: boolean;
  horizontal?: boolean;
  showLabel?: boolean;
  dataLabelTitle?: string;
  hideTooltip?: boolean;
  additionalComponents?: JSX.Element;
  isReport?: boolean;
  isEnForTest?: boolean;
  uploadedVideosTooltip?: string;
  customTooltip?: boolean;
  customTooltipData?: any;
}
const DataLabelChart: FunctionComponent<Props> = ({
  compId,
  showLegend = false,
  data,
  slug,
  showFilter,
  customFilters,
  tooltipTitle,
  channelTooltip,
  horizontal = true,
  showLabel = true,
  dataLabelTitle,
  hideTooltip,
  additionalComponents,
  isReport,
  isEnForTest,
  uploadedVideosTooltip,
  customTooltip,
  customTooltipData,
}) => {
  const isEn = getFromLocalStorage('LNG') == 'en' || isEnForTest ? true : false;

  const [filters, setFilters] = useState<any>(customFilters);
  const { theme } = useAppSettings();
  const { data: QData, isLoading } = useQuery(
    ['loadDataLabelChartData', { slug, body: filters }],
    loadDataLabelChartData,
    { enabled: !!slug },
  );

  const chartData = !!slug ? QData : data || { series: [], labels: [] };

  const handleChangeFilters = (option: any): void => {
    setFilters({ ...filters, time_interval: option.value });
  };
  return (
    <div id={compId}>
      <div id={`${compId}-DataLabelChart`} className={styles.DataLabel}>
        {dataLabelTitle && (
          <CardTitle
            title={dataLabelTitle}
            compId={compId}
            hideIcon={isReport}
          />
        )}
        <div
          className={styles.DataLabelContent}
          id={`${compId}-DataLabelChartContent`}
        >
          <div className={styles.TopBar}>
            <div className={styles.Select}>
              {showFilter && (
                <SelectInput
                  placeholder={_t('trends:Last_Day')}
                  onChange={handleChangeFilters}
                  options={time_interval}
                  name={''}
                />
              )}
            </div>
            <div className={styles.Controls}>
              {!isReport && <DotsMenu id={`${compId}-DataLabelChart`} />}
            </div>
          </div>
          <div className={isEn ? styles.center : ''}>
            {!isLoading ? (
              (chartData?.series?.length && (
                <div data-testid="chart-holder">
                  <ReactApexChart
                    options={
                      getDataLabelOptions(
                        theme.colors.chartsColors,
                        chartData?.labels,
                        showLegend,
                        chartData,
                        tooltipTitle,
                        channelTooltip,
                        horizontal,
                        showLabel,
                        hideTooltip,
                        isEn,
                        uploadedVideosTooltip,
                        customTooltip,
                        customTooltipData,
                      ) as any
                    }
                    series={[{ data: chartData.series }]}
                    type="bar"
                    height={180}
                    width={isEn ? '130%' : '104%'}
                  />
                </div>
              )) || <NoData />
            ) : (
              <Loader />
            )}
          </div>
        </div>
        {additionalComponents}
      </div>
    </div>
  );
};

function getDataLabelOptions(
  colors: string[],
  categories: string[],
  showLegend: boolean,
  chartData?: any,
  tooltipTitle?: string,
  channelTooltip?: boolean,
  horizontal?: boolean,
  showLabel?: boolean,
  hideTooltip?: boolean,
  isEn?: boolean,
  uploadedVideosTooltip?: string,
  customTooltip?: boolean,
  customTooltipData?: Record<string, number>[],
) {
  const options = {
    legend: {
      show: showLegend ? true : false,
      offsetX: showLegend ? 0 : 0,
      offsetY: showLegend ? 0 : 0,
      fontSize: '10px',
      fontFamily: ' LatoRegular, sans-serif',
      formatter: function (seriesName: any, value: any) {
        const totalSeriesValues = chartData.series.reduce(function (
          a: number,
          b: number,
        ) {
          return a + b;
        }, 0);
        const index = value.seriesIndex;
        const percentage = (
          (chartData.series?.[index] / totalSeriesValues) *
          100
        ).toFixed(1);
        seriesName =
          seriesName && seriesName.length > 10
            ? seriesName.slice(0, 10) + '...'
            : seriesName;
        return isEn
          ? isItArabicText(seriesName)
            ? ` <span>%${percentage}</span><span dir='auto'>${seriesName}</span>`
            : `<span dir='auto'>${seriesName} ${percentage}%<span>`
          : isItArabicText(seriesName)
            ? `<span dir='auto'>${seriesName} ${percentage}%</span>`
            : `<span>${percentage}%</span><span dir='auto'>${seriesName}</span>`;
      },
    },
    grid: {
      show: false,
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: false,
          formatter: (value: any) => nFormatter(Math.ceil(value)),
        },
      },
    },
    chart: {
      type: 'bar',
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        barHeight: '60%',
        distributed: true,
        horizontal: horizontal,
        rangeBarOverlap: true,
        rangeBarGroupRows: false,
        dataLabels: {
          position: 'bottom',
        },
      },
    },
    colors: colors,
    dataLabels: {
      enabled: true,
      textAnchor: 'start',
      style: {
        colors: ['#000'],
        fontSize: '4rem !important',
      },
      formatter: function (val: any) {
        return `${nFormatter(val)}`;
      },
      offsetY: 0,
      offsetX: isEn ? 300 : 350,
      dropShadow: {
        enabled: true,
      },
    },

    stroke: {
      width: 2,
      colors: colors,
    },
    xaxis: {
      categories: categories,
      labels: {
        show: showLabel,
        formatter: showLabel
          ? function (val: any) {
              return `${nFormatter(val?.toFixed(2))}`;
            }
          : undefined,
      },

      axisBorder: {
        show: false,
      },
    },
    yaxis: {
      labels: {
        show: false,
      },
    },
    tooltip: {
      enabled: hideTooltip ? false : true,
      // enabled: false,
      theme: 'white',
      custom: function ({ series, dataPointIndex }: any) {
        if (tooltipTitle) {
          return `
          <div class="videos_custom_tooltip">
          <div style="display: flex; align-items: center; justify-content: space-between;">
            <p>${_t('videos:published_on')}</p>
            <p><strong>${dateTimeFormatter.date(
              chartData.date[dataPointIndex],
            )}</strong></p>
          </div>
          <div style="display: flex; align-items: center; justify-content: space-between;margin-top: 20px;">
          <p>${_t('videos:gain')}</p>
          <p><strong>${nFormatter(series[0][dataPointIndex])}</strong> ${_t(
            tooltipTitle,
          )}</p>
          </div>
          </div>
          `;
        }
        if (channelTooltip) {
          return `<div class="videos_custom_tooltip">
          <div style="display: flex; align-items: center; justify-content: space-between;">
            <p>${_t('videos:joined')}</p>
            <p><strong>${dateTimeFormatter.date(
              chartData.date[dataPointIndex],
            )}</strong></p>
          </div>
          <div style="display: flex; align-items: center; justify-content: space-between;margin-top: 20px;">
          <p>${
            uploadedVideosTooltip === 'channel_total_subscribers'
              ? _t('videos:total_subs')
              : uploadedVideosTooltip === 'channel_total_views'
                ? _t('trends:total_views')
                : _t('videos:upload')
          }</p>
          <p><strong>${nFormatter(series[0][dataPointIndex])}</strong>${
            uploadedVideosTooltip === 'channel_total_views' ||
            uploadedVideosTooltip === 'channel_total_subscribers'
              ? ''
              : _t('videos:videos')
          }</p>
          </div>
          </div>`;
        }
        if (customTooltip && customTooltipData) {
          return `<p style="display:flex;justify-content: space-between; margin-bottom:12px;font-size:12px;">
                    <span>${_t('influencers:posts')}</span> : <span>${series[0][dataPointIndex]}</span>
                  <p>
                  <p style="display:flex;justify-content: space-between; margin-bottom:10px;font-size:12px;">
                    <span>${_t('hashtags:posts_type')}</span> : <span>${_t('basic:number')}</span>
                  <p>
                      <ul style="list-style-type: none;">
                      ${Object.entries(customTooltipData?.[dataPointIndex])
                        .map(
                          ([key, val]) =>
                            `<li style="text-transform: uppercase;font-size:10px;display:flex;justify-content: space-between; margin-bottom:10px;">
                            <span>${key}</span> <span>${val}</span>
                          </li>`,
                        )
                        .join('\n')}</ul>`;
        }
      },
      x: {
        show: true,
      },
      y: {
        title: {
          formatter: function () {
            return '';
          },
        },
      },
    },
  };
  return options;
}
export default DataLabelChart;
